import React, { useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    GuidanceRequest,
    JobApproval,
    SchemaInputFields,
    ServiceStatusBadge,
    apiErrorMessage,
    cleanInputValues,
    formatRelativeTime,
} from '../services/servicesShared';

const hasSchemaFields = (request: GuidanceRequest): boolean =>
    Boolean(request.input_schema?.properties && Object.keys(request.input_schema.properties).length);

/**
 * Answer one open question (POST /jobs/guidance/{id}/answer). Steward
 * questions carry a scope toggle: `job` applies to this customer only,
 * `service` becomes playbook guidance every job of the service reads at its
 * next wake (OWNED_JOBS_PLAN.md §1 Guidance).
 */
export const GuidanceAnswerForm: React.FC<{
    request: GuidanceRequest;
    onAnswered: (updated: GuidanceRequest) => void;
    compact?: boolean;
}> = ({ request, onAnswered, compact }) => {
    const { post } = useApi();
    const [text, setText] = useState('');
    const [values, setValues] = useState<Record<string, any>>({});
    const [scope, setScope] = useState<'job' | 'service'>('job');
    const [saving, setSaving] = useState(false);
    const isSteward = request.audience === 'steward';
    const schemaMode = hasSchemaFields(request);

    const pickOption = (option: string) => setText(option);

    const submit = async () => {
        const answers = schemaMode ? cleanInputValues(values) : null;
        if (!text.trim() && !(answers && Object.keys(answers).length)) {
            toast.error('Write an answer first');
            return;
        }
        setSaving(true);
        try {
            const response = await post(`/jobs/guidance/${request.id}/answer`, {
                text: text.trim() || null,
                answers,
                scope: isSteward ? scope : 'job',
            });
            toast.success(scope === 'service' && isSteward ? 'Answered — added to the service playbook' : 'Answered');
            onAnswered({ ...request, ...(response.data || {}), status: 'answered' });
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not send the answer'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`space-y-3 ${compact ? '' : 'rounded-lg border p-3'}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-medium">{request.question}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {request.audience === 'steward' ? 'Asked you' : 'Asked the customer'} · {request.mode === 'blocking' ? 'job is waiting' : 'job continues with its default'}
                        {request.deadline_at ? ` · falls back ${formatRelativeTime(request.deadline_at)}` : ''}
                        {request.coalesced_count && request.coalesced_count > 1 ? ` · ${request.coalesced_count} jobs hit this` : ''}
                    </div>
                </div>
                <ServiceStatusBadge status={request.status} />
            </div>
            {request.context && (
                <p className="text-xs text-muted-foreground whitespace-pre-wrap rounded bg-muted/50 p-2">{request.context}</p>
            )}
            {request.default_action && (
                <p className="text-xs text-muted-foreground">
                    Default if unanswered: <span className="text-foreground">{request.default_action}</span>
                </p>
            )}
            {request.status === 'open' ? (
                <>
                    {request.options?.length ? (
                        <div className="flex flex-wrap gap-1.5">
                            {request.options.map((option) => (
                                <Button
                                    key={option}
                                    type="button"
                                    size="sm"
                                    variant={text === option ? 'default' : 'outline'}
                                    onClick={() => pickOption(option)}
                                >
                                    {option}
                                </Button>
                            ))}
                        </div>
                    ) : null}
                    {schemaMode && <SchemaInputFields schema={request.input_schema} values={values} onChange={setValues} />}
                    <Textarea
                        rows={compact ? 2 : 3}
                        placeholder={schemaMode ? 'Anything else the job should know (optional)' : 'Your answer…'}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        {isSteward && (
                            <Select value={scope} onValueChange={(value) => setScope(value as 'job' | 'service')}>
                                <SelectTrigger className="w-[260px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="job">Applies to this job only</SelectItem>
                                    <SelectItem value="service">Applies to every job of this service</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        <Button size="sm" onClick={submit} disabled={saving}>
                            {saving ? 'Sending…' : 'Answer'}
                        </Button>
                    </div>
                </>
            ) : (
                (request.answer_text || request.answer) && (
                    <p className="text-sm">
                        <span className="text-muted-foreground">Answer: </span>
                        {request.answer_text || JSON.stringify(request.answer)}
                        {request.answer_scope === 'service' ? <span className="text-xs text-muted-foreground"> · service-wide</span> : null}
                    </p>
                )
            )}
        </div>
    );
};

/** Approve or decline a held write (POST /jobs/approvals/{id}/decide). */
export const ApprovalDecideCard: React.FC<{
    approval: JobApproval;
    onDecided: (updated: JobApproval) => void;
    compact?: boolean;
}> = ({ approval, onDecided, compact }) => {
    const { post } = useApi();
    const [note, setNote] = useState('');
    const [deciding, setDeciding] = useState<'approve' | 'decline' | null>(null);

    const decide = async (approve: boolean) => {
        setDeciding(approve ? 'approve' : 'decline');
        try {
            const response = await post(`/jobs/approvals/${approval.id}/decide`, { approve, note: note.trim() || null });
            toast.success(approve ? 'Approved — the job will run it' : 'Declined');
            onDecided({ ...approval, ...(response.data || {}), status: approve ? 'approved' : 'rejected' });
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not record the decision'));
        } finally {
            setDeciding(null);
        }
    };

    const argEntries = Object.entries(approval.args || {}).filter(([key]) => key !== '_reason');

    return (
        <div className={`space-y-3 ${compact ? '' : 'rounded-lg border p-3'}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-medium font-mono truncate" title={approval.tool}>
                        {approval.tool}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {approval.policy === 'ask_vendor' ? 'Needs your approval' : 'Needs the customer’s approval'} · asked{' '}
                        {formatRelativeTime(approval.created_at)}
                        {approval.expires_at ? ` · expires ${formatRelativeTime(approval.expires_at)}` : ''}
                    </div>
                </div>
                <ServiceStatusBadge status={approval.status} />
            </div>
            {approval.reason && <p className="text-sm whitespace-pre-wrap">{approval.reason}</p>}
            {argEntries.length > 0 && (
                <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground select-none">
                        {argEntries.length} argument{argEntries.length === 1 ? '' : 's'}
                    </summary>
                    <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
                        {JSON.stringify(Object.fromEntries(argEntries), null, 2)}
                    </pre>
                </details>
            )}
            {approval.status === 'pending' ? (
                <div className="space-y-2">
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Note (optional)</Label>
                        <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Why, or what to do instead" />
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={() => decide(true)} disabled={deciding !== null}>
                            {deciding === 'approve' ? 'Approving…' : 'Approve'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decide(false)} disabled={deciding !== null}>
                            {deciding === 'decline' ? 'Declining…' : 'Decline'}
                        </Button>
                    </div>
                </div>
            ) : (
                approval.decision_note && (
                    <p className="text-sm">
                        <span className="text-muted-foreground">Note: </span>
                        {approval.decision_note}
                    </p>
                )
            )}
        </div>
    );
};
